import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../App';
import { api } from '../api';
import { toast } from 'sonner';
import { formatPhone } from '../utils/formatPhone';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import {
  Users, Plane, Download, Save, Edit2, X as XIcon, Clock,
  CheckCircle, User, FileText, Printer, Building2, Package, ClipboardCheck
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import PageLayout from '../components/PageLayout';

const DEFAULT_SCHEDULE = `<p><strong>Saturday — Arrival Day</strong><br>Arrive Guatemala City 11am-2pm, shuttle to Antigua, team orientation</p>
<p><strong>Sunday — Setup &amp; Clinic</strong><br>Hospital tour, patient clinic, OR setup</p>
<p><strong>Mon-Thu — Surgery Days</strong><br>Breakfast 6:30am, First patient 7:30am, finish by 5pm</p>
<p><strong>Friday — Day Off</strong><br>Rounds, then free day for sightseeing</p>
<p><strong>Saturday — Departure</strong><br>Morning shuttle to Guatemala City airport</p>`;

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    ['clean']
  ],
};

const TripDetails = () => {
  const { tripId } = useParams();
  const { user, isAdmin } = useAuth();
  const [trip, setTrip] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingSchedule, setEditingSchedule] = useState(false);
  const [scheduleText, setScheduleText] = useState('');
  const [savingSchedule, setSavingSchedule] = useState(false);
  const [scheduleTemplates, setScheduleTemplates] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [tripRes, participantsRes, settingsRes] = await Promise.all([
          api.get(`/trips/${tripId}`),
          api.get(`/trips/${tripId}/participants`),
          api.get('/site-settings').catch(() => ({ data: {} })),
        ]);
        setTrip(tripRes.data);
        setParticipants(participantsRes.data);
        setScheduleText(tripRes.data.schedule_text || '');
        // Build template list from site settings
        const s = settingsRes.data || {};
        const templates = [];
        if (s.schedule_template_1) templates.push({ label: 'Template 1', content: s.schedule_template_1 });
        if (s.schedule_template_2) templates.push({ label: 'Template 2', content: s.schedule_template_2 });
        setScheduleTemplates(templates);
      } catch (error) {
        console.error('Error fetching trip:', error);
        toast.error('Failed to load trip details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [tripId]);

  const getDaysUntil = (dateStr) => {
    return Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
  };

  const handleExportRoster = async () => {
    try {
      const res = await api.get(`/export/roster/${tripId}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `roster_${trip.name.replace(/\s+/g, '_')}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Roster exported!');
    } catch { toast.error('Failed to export roster'); }
  };

  const handleSaveSchedule = async () => {
    setSavingSchedule(true);
    try {
      await api.put(`/trips/${tripId}`, { ...trip, schedule_text: scheduleText });
      setTrip(prev => ({ ...prev, schedule_text: scheduleText }));
      setEditingSchedule(false);
      toast.success('Schedule updated');
    } catch { toast.error('Failed to save schedule'); }
    finally { setSavingSchedule(false); }
  };

  if (loading) {
    return (
      <PageLayout title="Loading...">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="animate-pulse flex flex-col items-center gap-4">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-500">Loading trip...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  if (!trip) {
    return (
      <PageLayout title="Trip Not Found">
        <div className="flex items-center justify-center min-h-[50vh]">
          <div className="text-center">
            <h2 className="text-xl font-heading font-bold text-slate-800 mb-2">Trip Not Found</h2>
            <Link to="/dashboard"><Button className="rounded-full">Back to Dashboard</Button></Link>
          </div>
        </div>
      </PageLayout>
    );
  }

  const daysUntil = getDaysUntil(trip.start_date);
  const displaySchedule = trip.schedule_text || DEFAULT_SCHEDULE;

  const tripCountdown = daysUntil > 0 ? (
    <div className="hidden sm:block bg-primary/10 text-primary rounded-xl px-4 py-2 text-center">
      <p className="text-2xl font-heading font-bold">{daysUntil}</p>
      <p className="text-xs">days to go</p>
    </div>
  ) : null;

  return (
    <PageLayout title={trip.name} headerSub={`${trip.start_date} · ${trip.location}`} headerActions={tripCountdown} maxWidth="max-w-5xl">
          {/* Status badge and countdown for mobile */}
          <div className="flex flex-wrap items-center gap-3 mb-6">
            <Badge className={
              trip.status === 'registration_open' ? 'bg-primary/10 text-primary' :
              trip.status === 'active' ? 'bg-status-success/10 text-status-success' :
              'bg-slate-100 text-slate-600'
            }>
              {trip.status.replace('_', ' ')}
            </Badge>
            <div className="flex items-center gap-1 text-sm text-slate-500">
              <Users className="w-4 h-4" />
              <span>{trip.participant_count || 0} / {trip.max_participants} participants</span>
            </div>
            {daysUntil > 0 && (
              <div className="sm:hidden bg-primary/10 text-primary rounded-lg px-3 py-1 text-sm font-semibold">
                {daysUntil} days to go
              </div>
            )}
          </div>

          <div className="grid lg:grid-cols-3 gap-6 min-w-0">
          {/* Main content */}
          <div className="lg:col-span-2 space-y-6 min-w-0">
            {trip.description && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">About This Trip</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-slate-600 leading-relaxed">{trip.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Team Roster — Name + Role visible to everyone, full details for admin */}
            {participants.length > 0 && (
              <Card className="overflow-hidden">
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="font-heading flex items-center gap-2">
                      <Users className="w-5 h-5 text-primary" />
                      Team Roster
                    </CardTitle>
                    {isAdmin && (
                      <Button variant="outline" size="sm" className="rounded-full" onClick={handleExportRoster} data-testid="export-roster-btn">
                        <Download className="w-4 h-4 mr-2" />Export CSV
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Role</TableHead>
                          {isAdmin && <TableHead>Supply Bag</TableHead>}
                          {isAdmin && <TableHead>Arrival</TableHead>}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {participants.map((p) => (
                          <TableRow key={p.assignment_id || p.user_id}>
                            <TableCell>
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                                  <User className="w-4 h-4 text-primary" />
                                </div>
                                <p className="font-medium text-sm">
                                  {p.profile?.first_name || ''} {p.profile?.last_name || p.user?.name || 'Unknown'}
                                </p>
                              </div>
                            </TableCell>
                            <TableCell>
                              <span className="text-sm text-slate-600">
                                {p.profile?.work_roles?.join(', ') || '-'}
                              </span>
                            </TableCell>
                            {isAdmin && (
                              <TableCell>
                                {p.supply_bag ? (
                                  <Badge className="bg-primary/10 text-primary">{p.supply_bag.bag_name}</Badge>
                                ) : <span className="text-slate-400">-</span>}
                              </TableCell>
                            )}
                            {isAdmin && (
                              <TableCell className="text-sm">
                                {p.flight?.arrival_date ? (
                                  <div>
                                    <p>{p.flight.arrival_date}</p>
                                    <p className="text-slate-400">{p.flight.arrival_time}</p>
                                  </div>
                                ) : <span className="text-slate-400">Not set</span>}
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Schedule */}
            <Card className="overflow-hidden">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Clock className="w-5 h-5 text-primary" />
                    Trip Schedule
                  </CardTitle>
                  {isAdmin && !editingSchedule && (
                    <Button variant="ghost" size="sm" onClick={() => { setScheduleText(trip.schedule_text || DEFAULT_SCHEDULE); setEditingSchedule(true); }} data-testid="edit-schedule-btn">
                      <Edit2 className="w-4 h-4 mr-1" />Edit
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {editingSchedule ? (
                  <div className="space-y-3">
                    {scheduleTemplates.length > 0 && (
                      <div className="flex items-center gap-2">
                        <span className="text-xs text-slate-500">Load template:</span>
                        {scheduleTemplates.map((t, i) => (
                          <Button
                            key={i}
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs rounded-full"
                            onClick={() => {
                              if (scheduleText && !window.confirm('Replace current schedule with this template?')) return;
                              setScheduleText(t.content);
                              toast.success(`${t.label} loaded — edit and save`);
                            }}
                            data-testid={`load-template-${i}`}
                          >
                            {t.label}
                          </Button>
                        ))}
                      </div>
                    )}
                    <div data-testid="schedule-editor">
                      <ReactQuill
                        theme="snow"
                        value={scheduleText}
                        onChange={setScheduleText}
                        modules={QUILL_MODULES}
                        placeholder="Enter the trip schedule..."
                      />
                    </div>
                    <div className="flex gap-2 mt-2">
                      <Button onClick={handleSaveSchedule} disabled={savingSchedule} className="rounded-full bg-rojo hover:bg-rojo-dark" data-testid="save-schedule-btn">
                        <Save className="w-4 h-4 mr-1" />{savingSchedule ? 'Saving...' : 'Save Schedule'}
                      </Button>
                      <Button variant="outline" className="rounded-full" onClick={() => setEditingSchedule(false)}>
                        <XIcon className="w-4 h-4 mr-1" />Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="text-sm text-slate-600 leading-relaxed prose prose-sm max-w-none overflow-hidden break-words"
                    dangerouslySetInnerHTML={{ __html: displaySchedule }}
                  />
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Quick Actions */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">My Tasks</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <Link to="/profile" className="block">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <User className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">Complete Profile</span>
                    </div>
                    <CheckCircle className="w-4 h-4 text-slate-300" />
                  </div>
                </Link>
                <Link to="/waiver" className="block">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">Sign Waiver</span>
                    </div>
                    <CheckCircle className="w-4 h-4 text-slate-300" />
                  </div>
                </Link>
                <Link to="/flight-hotel" className="block">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors" data-testid="flight-info-link">
                    <div className="flex items-center gap-3">
                      <Plane className="w-5 h-5 text-primary" />
                      <span className="text-sm font-medium">Flight & Hotel</span>
                    </div>
                    <Clock className="w-4 h-4 text-status-warning" />
                  </div>
                </Link>
              </CardContent>
            </Card>

            {/* Important Links */}
            <Card>
              <CardHeader>
                <CardTitle className="font-heading">Resources</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a href="https://give.commonhope.org/campaign/guatemala-surgery/c222592" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <span className="text-lg">💝</span>
                  <span className="text-sm font-medium">Make a Donation</span>
                </a>
                <a href="https://www.commonhope.org" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <span className="text-lg">🌍</span>
                  <span className="text-sm font-medium">Common Hope</span>
                </a>
                <a href="https://step.state.gov/step/" target="_blank" rel="noopener noreferrer"
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors">
                  <span className="text-lg">🛂</span>
                  <span className="text-sm font-medium">STEP Program</span>
                </a>
              </CardContent>
            </Card>

            {/* Admin Actions */}
            {isAdmin && (
              <Card>
                <CardHeader>
                  <CardTitle className="font-heading">Admin Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Button variant="outline" className="w-full justify-start rounded-xl" onClick={handleExportRoster}>
                    <Download className="w-4 h-4 mr-2" />Export CSV
                  </Button>
                  <Button variant="outline" className="w-full justify-start rounded-xl"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/export/roster-html/${tripId}`, '_blank')}
                    data-testid="print-roster-btn">
                    <Printer className="w-4 h-4 mr-2" />Printable Roster
                  </Button>
                  <Link to="/admin/participants" className="block">
                    <Button variant="outline" className="w-full justify-start rounded-xl">
                      <Users className="w-4 h-4 mr-2" />Manage Participants
                    </Button>
                  </Link>
                  <Link to={`/trips/${tripId}/rooms`} className="block">
                    <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="room-assignments-link">
                      <Building2 className="w-4 h-4 mr-2" />Room Assignments
                    </Button>
                  </Link>
                  <Link to={`/admin/bags?trip_id=${tripId}`} className="block">
                    <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="transport-bags-link">
                      <Package className="w-4 h-4 mr-2" />Transport Bags
                    </Button>
                  </Link>
                  <Link to={`/admin/count-session?trip_id=${tripId}`} className="block">
                    <Button variant="outline" className="w-full justify-start rounded-xl" data-testid="count-session-link">
                      <ClipboardCheck className="w-4 h-4 mr-2" />Inventory Count
                    </Button>
                  </Link>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
    </PageLayout>
  );
};

export default TripDetails;
